import React from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import ErrorBoundary from './components/ErrorBoundary'
import Layout from './components/Layout'
import Login from './pages/Login'
import Signup from './pages/Signup'
import Dashboard from './pages/Dashboard'
import { ClientsList, ClientProfile, NewClient } from './pages/Clients'
import { JobsList, JobDetail, NewJob } from './pages/Jobs'
import Clock from './pages/Clock'
import Employees from './pages/Employees'
import Estimates from './pages/Estimates'
import EstimateBuilder from './pages/EstimateBuilder'
import { SiteVisitsList, SiteVisitDetail } from './pages/SiteVisits'
import Proposals from './pages/Proposals'
import ProposalPreview from './pages/ProposalPreview'
import MaterialPriceList from './pages/MaterialPriceList'
import Financials from './pages/Financials'
import Settings from './pages/Settings'
import Schedule from './pages/Schedule'
import { InvoicesList, InvoiceDetail, NewStandaloneInvoice } from './pages/Invoices'

// ── Client Portal ──────────────────────────────────────────────────────────────
import { PortalAuthProvider } from './context/PortalAuthContext'
import PortalRoute            from './components/PortalRoute'
import PortalLogin            from './pages/portal/PortalLogin'
import PortalDashboard        from './pages/portal/PortalDashboard'
import PortalEstimates        from './pages/portal/PortalEstimates'
import PortalEstimateDetail   from './pages/portal/PortalEstimateDetail'
import PortalInvoices         from './pages/portal/PortalInvoices'
import PortalInvoiceDetail    from './pages/portal/PortalInvoiceDetail'

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading, isAdmin } = useAuth()
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: "'DM Sans', sans-serif", color: '#6b7c6b' }}>
      Loading TLC Unlimited...
    </div>
  )
  if (!user) return <Navigate to="/login" />
  if (adminOnly && !isAdmin) return <Navigate to="/" />
  return <Layout>{children}</Layout>
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <PortalAuthProvider>
          <BrowserRouter>
            <Routes>
              {/* ── Admin / Staff routes ── */}
              <Route path="/login"   element={<LoginRoute />} />
              <Route path="/signup"  element={<Signup />} />
              <Route path="/"        element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/jobs"    element={<ProtectedRoute><JobsList /></ProtectedRoute>} />
              <Route path="/jobs/new"    element={<ProtectedRoute><NewJob /></ProtectedRoute>} />
              <Route path="/jobs/:id"    element={<ProtectedRoute><JobDetail /></ProtectedRoute>} />
              <Route path="/clients"     element={<ProtectedRoute><ClientsList /></ProtectedRoute>} />
              <Route path="/clients/new" element={<ProtectedRoute adminOnly><NewClient /></ProtectedRoute>} />
              <Route path="/clients/:id" element={<ProtectedRoute><ClientProfile /></ProtectedRoute>} />
              <Route path="/employees"   element={<ProtectedRoute adminOnly><Employees /></ProtectedRoute>} />
              <Route path="/estimates"      element={<ProtectedRoute adminOnly><Estimates /></ProtectedRoute>} />
              <Route path="/estimates/new"  element={<ProtectedRoute adminOnly><EstimateBuilder /></ProtectedRoute>} />
              <Route path="/estimates/:id/edit" element={<ProtectedRoute adminOnly><EstimateBuilder /></ProtectedRoute>} />
              <Route path="/proposals"     element={<ProtectedRoute adminOnly><Proposals /></ProtectedRoute>} />
              <Route path="/proposals/:id" element={<ProtectedRoute adminOnly><ProposalPreview /></ProtectedRoute>} />
              {/* Not adminOnly: employees can log their own site visits in the field before
                  pricing exists — RLS scopes them to visits they created (see SCHEMA.md). */}
              <Route path="/site-visits"     element={<ProtectedRoute><SiteVisitsList /></ProtectedRoute>} />
              <Route path="/site-visits/:id" element={<ProtectedRoute><SiteVisitDetail /></ProtectedRoute>} />
              <Route path="/materials"   element={<ProtectedRoute adminOnly><MaterialPriceList /></ProtectedRoute>} />
              <Route path="/financials"  element={<ProtectedRoute adminOnly><Financials /></ProtectedRoute>} />
              <Route path="/clock"       element={<ProtectedRoute><Clock /></ProtectedRoute>} />
              <Route path="/settings"    element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              {/* Not adminOnly: employees see only their own assigned jobs here via the
                  existing jobs RLS policy, matching PRODUCT_PRINCIPLES.md's documented
                  "Field employee: Job, schedule, notes" persona (never wired up before). */}
              <Route path="/schedule"    element={<ProtectedRoute><Schedule /></ProtectedRoute>} />
              <Route path="/invoices"    element={<ProtectedRoute adminOnly><InvoicesList /></ProtectedRoute>} />
              <Route path="/invoices/new" element={<ProtectedRoute adminOnly><NewStandaloneInvoice /></ProtectedRoute>} />
              <Route path="/invoices/:id" element={<ProtectedRoute adminOnly><InvoiceDetail /></ProtectedRoute>} />

              {/* ── Client Portal routes ── */}
              <Route path="/portal"               element={<Navigate to="/portal/login" replace />} />
              <Route path="/portal/login"         element={<PortalLogin />} />
              <Route path="/portal/dashboard"     element={<PortalRoute><PortalDashboard /></PortalRoute>} />
              <Route path="/portal/estimates"     element={<PortalRoute><PortalEstimates /></PortalRoute>} />
              <Route path="/portal/estimates/:id" element={<PortalRoute><PortalEstimateDetail /></PortalRoute>} />
              <Route path="/portal/invoices"      element={<PortalRoute><PortalInvoices /></PortalRoute>} />
              <Route path="/portal/invoices/:id"  element={<PortalRoute><PortalInvoiceDetail /></PortalRoute>} />
            </Routes>
          </BrowserRouter>
        </PortalAuthProvider>
      </AuthProvider>
    </ErrorBoundary>
  )
}

function LoginRoute() {
  const { user, loading } = useAuth()
  if (loading) return null
  if (user) return <Navigate to="/" />
  return <Login />
}
